import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Brain, Lock, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { adoptSession } = useAuth();
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      adoptSession(data.token, data.user);
      toast.success("Password updated — welcome back.");
      nav(data.user?.role === "coach" ? "/coach" : data.user?.role === "athletic_director" ? "/ad" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Shell>
        <div className="text-xs tracking-widest text-blaze">MISSING TOKEN</div>
        <h1 className="heading text-4xl mt-2">LINK INCOMPLETE</h1>
        <p className="text-neutral-400 text-sm mt-3">
          This reset link is missing its token. Please open the link directly from the email we sent you,
          or request a new reset.
        </p>
        <Link
          to="/forgot-password"
          data-testid="reset-request-new-btn"
          className="mt-6 w-full min-h-[48px] bg-blaze text-white heading text-lg hover:glow-blaze transition-all flex items-center justify-center"
        >
          REQUEST NEW LINK
        </Link>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div className="flex items-center gap-2 text-volt text-xs tracking-widest">
          <CheckCircle2 size={14} /> RESET LINK VERIFIED
        </div>
        <h1 className="heading text-4xl">PICK A NEW PASSWORD</h1>
        <p className="text-neutral-400 text-sm">
          At least 6 characters. You&apos;ll be signed in automatically after saving.
        </p>
        <div>
          <div className="text-[11px] tracking-widest text-neutral-500 mb-1.5">NEW PASSWORD</div>
          <input
            data-testid="reset-password-input"
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-appcard border border-neutral-800 px-3.5 py-3 text-sm outline-none focus:border-blaze min-h-[44px]"
            placeholder="6+ characters"
          />
        </div>
        <div>
          <div className="text-[11px] tracking-widest text-neutral-500 mb-1.5">CONFIRM PASSWORD</div>
          <input
            data-testid="reset-confirm-input"
            type="password"
            required
            minLength={6}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full bg-appcard border border-neutral-800 px-3.5 py-3 text-sm outline-none focus:border-blaze min-h-[44px]"
            placeholder="Repeat it"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="reset-submit-btn"
          className="w-full min-h-[48px] bg-blaze text-white heading text-lg tracking-wider hover:glow-blaze transition-all disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Lock size={16} /> {busy ? "SAVING…" : "SET NEW PASSWORD"}
        </button>
        <Link
          to="/auth"
          data-testid="reset-cancel-link"
          className="block text-center text-neutral-500 text-xs hover:text-volt transition-colors"
        >
          Cancel and go to login
        </Link>
      </form>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-appbg text-white flex items-center justify-center p-6">
      <div className="w-full max-w-md" data-testid="reset-password-page">
        <Link to="/" className="flex items-center gap-2 mb-8" data-testid="reset-logo-link">
          <Brain className="text-blaze" size={22} />
          <span className="heading text-2xl">CoachConnect <span className="text-blaze">AI</span></span>
        </Link>
        <div className="border border-neutral-800 bg-appcard p-8">{children}</div>
      </div>
    </div>
  );
}
